/*
 * Copyright (c) 2023-2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {GpsRpo} from './gps-rpo';
import {MomentumProgram} from './momentumProgram.model';
import {CommonHelperService} from '../service/common-helper.service';
import {ValidationModel} from './validation.model';

export class GpsTiming extends ValidationModel {
  id: number;
  gpsRpo: GpsRpo;
  momentumProgram: MomentumProgram;
  released: string;
  dynoRequired: string;
  // latest from momentum
  mrd: Date;
  mrdType: string;
  fourWeekNotificationEmail: Date;
  twoWeekNotificationEmail: Date;
  oneWeekNotificationEmail: Date;
  oneDayLateEscalationEmail: Date;
  sixteenWeeksToMrd: Date;
  twentyOneWeeksToMrd: Date;
  twentyFiveWeeksToMrd: Date;
  twentyNineWeeksToMrd: Date;
  thirtyOneWeeksToMrd: Date;
  vehicleStage3Rcd: Date;
  adjustedWeeks: number;
  // if an mrd is stored in the db, we can compare it to what comes from momentum
  originalMrd: Date;
  notes: string;
  privateNotes: string;

  fieldRules = [
    {fieldName: 'released', required: false, maxLength: 50},
    {fieldName: 'dynoRequired', required: false, maxLength: 50},
    {fieldName: 'notes', required: false, maxLength: 500},
    {fieldName: 'privateNotes', required: false, maxLength: 500},
    {fieldName: 'vehicleStage3Rcd', required: false, maxLength: 50, date: true},
  ];
  // fields for binding to the java model
  gpsRpoId: number;
  modelYear: string;
  gpsProgramClassification: string;
  gpsType: string;
  gpsProgramFamily: string;
  vehicleProgramCode: string;
  labelForDropdown: string;
  // used to tell each cell in the grid if user can edit
  hasAdminPermission: boolean;

  setGpsTimingDates() {
    if (this.momentumProgram['date12152']) {
      this.mrd = CommonHelperService.calculateDateWithOffset(this.momentumProgram['date12152']);
      this.mrdType = 'Beta';
    } else if (this.momentumProgram['date12128']) {
      this.mrd = CommonHelperService.calculateDateWithOffset(this.momentumProgram['date12128']);
      this.mrdType = 'Gamma';
    } else if (this.momentumProgram['date12236']) {
      this.mrd = CommonHelperService.calculateDateWithOffset(this.momentumProgram['date12236']);
      this.mrdType = 'PPV';
    } else if (this.momentumProgram['date12451']) {
      this.mrd = CommonHelperService.calculateDateWithOffset(this.momentumProgram['date12451']);
      this.mrdType = 'RLM';
    }
  }

  public calculateDates() {
    const offset = this.adjustedWeeks || 0;
    if (this.mrd) {
      const mrdString = this.mrd.toString();
      this.sixteenWeeksToMrd = CommonHelperService.calculateDateWithOffset(mrdString, -16 + offset);
      this.twentyOneWeeksToMrd = CommonHelperService.calculateDateWithOffset(mrdString, -21 + offset);
      this.twentyFiveWeeksToMrd = CommonHelperService.calculateDateWithOffset(mrdString, -25 + offset);
      this.twentyNineWeeksToMrd = CommonHelperService.calculateDateWithOffset(mrdString, -29 + offset);
      this.thirtyOneWeeksToMrd = CommonHelperService.calculateDateWithOffset(mrdString, -31 + offset);
      this.fourWeekNotificationEmail = CommonHelperService.calculateDateWithOffset(this.twentyOneWeeksToMrd + '', -4 + offset);
      this.twoWeekNotificationEmail = CommonHelperService.calculateDateWithOffset(this.twentyOneWeeksToMrd + '', -2 + offset);
      this.oneWeekNotificationEmail = CommonHelperService.calculateDateWithOffset(this.twentyOneWeeksToMrd + '', -1 + offset);
      this.oneDayLateEscalationEmail = CommonHelperService.calculateDateWithOffset(this.twentyOneWeeksToMrd + '', offset);
      this.oneDayLateEscalationEmail.setDate(this.twentyOneWeeksToMrd.getDate() + 1);
    }
  }

// This method takes what comes from esci db and sets it into a model we can use
  setData(result) {
    this.id = result.id;
    this.released = result.released;
    this.dynoRequired = result.dynoRequired;
    this.mrdType = result.mrdType;
    this.gpsRpoId = result.gpsRpoId;
    this.modelYear = result.modelYear;
    this.gpsProgramClassification = result.gpsProgramClassification;
    this.notes = result.notes;
    this.privateNotes = result.privateNotes;
    this.gpsType = result.gpsType;
    this.gpsProgramFamily = result.gpsProgramFamily;
    this.vehicleProgramCode = result.vehicleProgramCode;
    if (this.vehicleProgramCode) {
      this.labelForDropdown = `${this.modelYear} ${this.vehicleProgramCode} (${this.mrdType}) - ${this.gpsProgramClassification}`;
    }
    this.adjustedWeeks = result.adjustedWeeks;
    if (result.mrd) {
        this.mrd = CommonHelperService.calculateDateWithOffset(new Date(result.mrd) + '');
        this.originalMrd = CommonHelperService.calculateDateWithOffset(new Date(result.mrd) + '');
    }
    if (result.vehicleStage3Rcd) {
        this.vehicleStage3Rcd = CommonHelperService.calculateDateWithOffset(new Date(result.vehicleStage3Rcd) + '');
    }
  }
}
